/**
 * Agent list page — FE-22.
 * Filter by governance_status, search, paginated table, click to detail.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type AgentSummary,
  type GovernanceStatus,
  fetchAgents,
} from "../../api/admin-agents";
import {
  Pagination,
  Td,
  Th,
  fmtDate,
  tableStyles,
} from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { StatsBar } from "../../components/StatsBar";
import { t } from "../../i18n";

const PAGE_SIZE = 20;
const STATUS_OPTIONS = [
  "all",
  "active",
  "restricted",
  "suspended",
  "banned",
] as const;

export function AgentsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<AgentSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAgents({
        q: query || undefined,
        governance_status:
          status === "all" ? undefined : (status as GovernanceStatus),
        limit: PAGE_SIZE,
        offset,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("agents.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, [query, status, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSearch = useCallback((): void => {
    setOffset(0);
  }, []);

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <PageHeader
        title={t("agents.title")}
        description={t("agents.description")}
      />
      <StatsBar items={[{ label: t("stats.total"), value: total }]} />
      <FilterBar
        query={query}
        status={status}
        onQueryChange={setQuery}
        onStatusChange={(v) => {
          setStatus(v);
          setOffset(0);
        }}
        onSearch={handleSearch}
      />
      {error && <p style={s.error}>{error}</p>}
      {loading ? (
        <p style={s.loadingText}>{t("agents.loading")}</p>
      ) : (
        <>
          <AgentTable
            items={items}
            onRowClick={(id) => navigate(`/governance/agents/${id}`)}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={(p) => setOffset((p - 1) * PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}

// ─── FilterBar ───

function FilterBar(props: {
  query: string;
  status: string;
  onQueryChange: (v: string) => void;
  onStatusChange: (v: string) => void;
  onSearch: () => void;
}): React.JSX.Element {
  return (
    <div style={s.filterRow}>
      <input
        type="text"
        placeholder={t("agents.search_placeholder")}
        value={props.query}
        onChange={(e) => props.onQueryChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") props.onSearch();
        }}
        style={s.searchInput}
      />
      <select
        value={props.status}
        onChange={(e) => props.onStatusChange(e.target.value)}
        style={s.filterSelect}
        aria-label="Filter by status"
      >
        {STATUS_OPTIONS.map((v) => (
          <option key={v} value={v}>
            {v === "all" ? t("agents.all_status") : capitalize(v)}
          </option>
        ))}
      </select>
      <button type="button" onClick={props.onSearch} style={s.filterBtn}>
        Search
      </button>
    </div>
  );
}

// ─── Table ───

const COLUMN_KEYS = [
  "agents.col_name",
  "agents.col_creator",
  "agents.col_connection",
  "agents.col_status",
  "agents.col_risk",
  "agents.col_reports",
  "agents.col_created",
] as const;

function AgentTable(props: {
  items: AgentSummary[];
  onRowClick: (id: string) => void;
}): React.JSX.Element {
  if (props.items.length === 0) {
    return <p style={s.empty}>{t("agents.no_agents_found")}</p>;
  }
  return (
    <table style={s.table}>
      <thead>
        <tr>
          {COLUMN_KEYS.map((c) => (
            <Th key={c}>{t(c)}</Th>
          ))}
        </tr>
      </thead>
      <tbody>
        {props.items.map((a) => (
          <AgentRow key={a.id} agent={a} onClick={props.onRowClick} />
        ))}
      </tbody>
    </table>
  );
}

function AgentRow(props: {
  agent: AgentSummary;
  onClick: (id: string) => void;
}): React.JSX.Element {
  const { agent: a } = props;
  return (
    <tr
      style={s.clickableRow}
      onClick={() => props.onClick(a.id)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") props.onClick(a.id);
      }}
    >
      <Td>{a.name}</Td>
      <Td>{a.creator_name}</Td>
      <Td>{a.connection_mode}</Td>
      <Td>
        <StatusBadge status={a.governance_status} />
      </Td>
      <Td>{a.risk_score}</Td>
      <Td>{a.report_count}</Td>
      <Td>{fmtDate(a.created_at)}</Td>
    </tr>
  );
}

// ─── StatusBadge ───

function StatusBadge({
  status,
}: { status: GovernanceStatus }): React.JSX.Element {
  const c = GOVERNANCE_COLOR[status];
  return (
    <span style={{ ...s.badge, background: c.bg, color: c.fg }}>
      {capitalize(status)}
    </span>
  );
}

const GOVERNANCE_COLOR: Record<GovernanceStatus, { bg: string; fg: string }> = {
  active: { bg: "#dcfce7", fg: "#166534" },
  restricted: { bg: "#fef9c3", fg: "#854d0e" },
  suspended: { bg: "#fef2f2", fg: "#991b1b" },
  banned: { bg: "#f3f4f6", fg: "#6b7280" },
};

// ─── Helpers ───

function capitalize(v: string): string {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  ...tableStyles,
  searchInput: {
    flex: 1,
    minWidth: 200,
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
  },
  filterSelect: {
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
    background: "var(--color-bg)",
  },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
  },
  clickableRow: { cursor: "pointer" },
  empty: {
    fontSize: 13,
    color: "var(--color-text-secondary)",
    padding: 24,
    textAlign: "center",
  },
};
